import { Component, NgZone, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import PocketBase, { UnsubscribeFunc } from 'pocketbase';
import { IRequestPayment } from 'src/app/shared/interfaces/interface';
import { CommonService } from 'src/app/services/common/common.service';
import { DataService } from 'src/app/services/data/data.service';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { AppRoute } from 'src/app/shared/constants/app-route.constant';
import {
  InterfaceErrorText,
  InterfaceText,
  OmiseStatusName,
  PaymentChannel,
  PaymentCurrency,
  PaymentDescription,
  PaymentType,
  StorageKey,
} from 'src/app/shared/constants/global-config.constant';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';

/**
 * How often the background check asks the API whether the payment landed. Faster than the
 * other methods (10s): for cards the server marks `is_paid` itself the moment Omise confirms,
 * so this poll is the primary signal, not just a backstop — see startPaymentPolling().
 */
const PAYMENT_POLL_INTERVAL_MS = 3000;

/**
 * /payment-creditcard — CREDITCARD via the hosted card page in sc-screenlock-v2-api.
 *
 * Same shape as /payment-link: the kiosk creates the payment, shows `ref_id_3` as a QR, and
 * waits. Differences: `ref_id_3` is our own page (omise.js card form) rather than an Omise
 * link; the charge is created later from the customer's phone, so there is no charge id to
 * look up in Pocketbase — the realtime subscription filters the collection by `trx_id`
 * instead; BACK cancels the payment server-side so the phone page refuses to charge.
 */
@Component({
  selector: 'app-payment-creditcard',
  templateUrl: './payment-creditcard.component.html',
  styleUrls: ['./payment-creditcard.component.scss']
})
export class PaymentCreditcardComponent implements OnDestroy {

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dataService: DataService,
    private themeService: ThemeService,
    public commonService: CommonService,
    private ngZone: NgZone,
  ) { }

  TXT_PAYMENT_SCAN_SCULPTURE = InterfaceText.PAYMENT_SCAN_SCULPTURE;
  TXT_PAYMENT_CREDITCARD_TITLE = InterfaceText.PAYMENT_CREDITCARD_TITLE;
  TXT_PAYMENT_CREDITCARD_SUBTITLE = InterfaceText.PAYMENT_CREDITCARD_SUBTITLE;
  TXT_PAYMENT_SCAN_QR_ERROR_TITLE = InterfaceErrorText.PAYMENT_SCAN_QR_ERROR_TITLE;
  TXT_PAYMENT_SCAN_QR_ERROR_SUBTITLE = InterfaceErrorText.PAYMENT_SCAN_QR_ERROR_SUBTITLE;
  TXT_PAYMENT_SCAN_CURRENCY = InterfaceText.PAYMENT_SCAN_CURRENCY;

  theme = this.themeService.getTheme();

  promoType: string = null;
  totalPrice: number = 0;
  vatPrice: number = 0;
  discountPrice: number = 0;
  deductedPrice: number = 0;

  amountSum: number = 0;

  SculptureTrxId: string = null;
  SculpturePaymentId: string = null;
  /** The hosted card page URL (`ref_id_3`) rendered as the QR. */
  CardPageUrl: string = null;

  isQrError: boolean = false;

  /** Guards against the realtime callback and the background poll both completing the payment. */
  private isPaymentHandled: boolean = false;
  private pollTimer: any = null;
  private queryParamsSub: Subscription;
  private realtimeUnsubscribe: UnsubscribeFunc | null = null;

  pbClient = new PocketBase(environment.pocket_endpoint);

  ngOnDestroy(): void {
    this.stopPaymentPolling();
    this.unsubscribeRealtime();

    if (this.queryParamsSub) {
      this.queryParamsSub.unsubscribe();
    }
  }

  async ngOnInit() {
    this.queryParamsSub = this.activatedRoute.queryParams.subscribe(params => {
      this.promoType = params.promo_type;
      this.totalPrice = params.total_price;
      this.vatPrice = params.vat_price;
      this.discountPrice = params.discount_price;
      this.deductedPrice = params.deducted_price;

      this.getCardPageQR();
    });
    this.amountSum = this.totalPrice;
  }

  async getCardPageQR() {
    try {
      this.commonService.isLoading = true;
      const payload: IRequestPayment = {
        branch_code: localStorage.getItem(StorageKey.BRANCH_CODE),
        payment_type: PaymentType.CREDITCARD,
        channel: PaymentChannel.OMISE,
        // Guard at the boundary as well as at source: these arrive as query params,
        // and the API rejects anything with more than two decimals.
        amount_net: this.commonService.roundNumberToTwoDecimalPlaces(Number(this.deductedPrice)),
        amount_vat: this.commonService.roundNumberToTwoDecimalPlaces(Number(this.vatPrice)),
        description: PaymentDescription.DEFAULT,
        amount_discount: this.commonService.roundNumberToTwoDecimalPlaces(Number(this.discountPrice)),
        currency: PaymentCurrency.THB,
      };
      const result = await this.dataService.getCreatePayment(payload).toPromise();

      this.SculptureTrxId = result.trx_id;
      this.SculpturePaymentId = result.id.toString();
      this.CardPageUrl = result.ref_id_3;

      if (!this.CardPageUrl) {
        this.isQrError = true;
      }
      else {
        // No delay needed: we subscribe by trx_id, so nothing has to exist in Pocketbase yet.
        this.subscribeToCheckPaid();
        this.startPaymentPolling();
      }
    }
    catch (err) {
      this.isQrError = true;
      console.log(err);
    }
    finally {
      this.commonService.isLoading = false;
    }
  }

  /**
   * Realtime fast path. core-api's webhook mirrors every card charge event into the
   * `omise_trx_*` collection with the payment's `trx_id`, so we can listen for our own
   * payment before any charge exists. Every failure is swallowed on purpose: the poll started
   * alongside is then the only listener, which is enough.
   */
  async subscribeToCheckPaid() {
    try {
      await this.pbClient.collection('co_app_users').authWithPassword(
        environment.pocket_acc,
        localStorage.getItem(StorageKey.POCKET_KEY)
      );

      console.log('pocketbase subscribing to collection by trx_id: CREDITCARD');
      // Inside the zone on purpose. The Pocketbase SDK reconnects its EventSource from
      // `eventSource.onerror`, which zone.js does not patch, so after the first drop every
      // event arrives in the root zone; a navigation from there changes the URL and draws
      // nothing. See payment-promptpay.component.spec.ts for the booth history.
      this.realtimeUnsubscribe = await this.pbClient
        .collection(environment.pocket_group)
        .subscribe('*', (event) => this.ngZone.run(async () => {
          const record: any = event.record;
          // The server-side filter needs the `trx_id` field to exist on the collection; the
          // client-side check below makes this correct either way.
          if (record && record.trx_id === this.SculptureTrxId && record.status === OmiseStatusName.SUCCESS) {
            await this.handlePaymentPaid();
          }
        }), { filter: `trx_id='${this.SculptureTrxId}'` });
    }
    catch (err) {
      console.log('subscribeToCheckPaid failed ~ relying on payment polling', err);
    }
  }

  private startPaymentPolling() {
    if (this.pollTimer !== null || this.isPaymentHandled || this.SculptureTrxId === null) {
      return;
    }

    console.log('payment polling started every', PAYMENT_POLL_INTERVAL_MS, 'ms');
    this.pollTimer = setInterval(() => this.pollPaymentOnce(), PAYMENT_POLL_INTERVAL_MS);
  }

  private async pollPaymentOnce() {
    if (this.isPaymentHandled) {
      return;
    }

    try {
      // Silent: no loading overlay, no "not paid yet" popup, no retry toast (which would
      // close an open dialog). A background check has to be invisible until it finds something.
      const payment = await this.dataService.getPaymentInfo(this.SculptureTrxId, true).toPromise();

      if (payment && payment.is_paid) {
        await this.handlePaymentPaid();
      }
    }
    catch (err) {
      // Stay quiet and try again on the next tick.
    }
  }

  private stopPaymentPolling() {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  /**
   * The single place a confirmed payment is acted on, so the realtime callback, the poll
   * and the CONFIRM button cannot navigate to the success page more than once between them.
   */
  private async handlePaymentPaid() {
    if (this.isPaymentHandled) {
      return;
    }
    this.isPaymentHandled = true;

    this.stopPaymentPolling();
    await this.updatePaymentDone();
    this.unsubscribeRealtime();
    this.goToSuccess();
  }

  private unsubscribeRealtime() {
    if (this.realtimeUnsubscribe === null) {
      return;
    }
    const unsubscribe = this.realtimeUnsubscribe;
    this.realtimeUnsubscribe = null;
    unsubscribe().catch(err => console.log('realtime unsubscribe failed', err));
  }

  async updatePaymentDone() {
    this.commonService.isLoading = true;
    try {
      this.commonService.setPaymentTrxId(this.SculptureTrxId);

      // The server already set is_paid for cards; this keeps date_paid_local and the promo
      // bookkeeping identical to the other methods. A 400 "already paid" is expected, so it
      // sits in its own try and never skips marking the promo used.
      try {
        const payload1 = { is_paid: true };
        await this.dataService.updatePaymentByTrxId(this.SculptureTrxId, payload1).toPromise();
      }
      catch (err) {
        console.log('payment already updated, just to make sure', JSON.stringify(err));
      }
      if (localStorage.getItem(StorageKey.PROMO_ID) !== null) {
        const payload3: any = { is_used: true, payment_id: this.SculpturePaymentId };
        await this.dataService.updatePromotionById(localStorage.getItem(StorageKey.PROMO_ID), payload3).toPromise();
      }
    }
    catch (err) {
      console.log(JSON.stringify(err));
      if (err.status === 400) {
        console.log('payment already updated, just to make sure');
      }
    }
    finally {
      this.commonService.isLoading = false;
    }
  }

  goToSuccess() {
    this.router.navigate([AppRoute.PAYMENT_SUCCESS]);
  }

  /**
   * Marks the payment cancelled server-side so the phone page refuses to charge a customer
   * whose kiosk session has already moved on. Best effort: a 400 means it is already
   * paid/cancelled, and either way the kiosk leaves this page.
   */
  private async cancelPayment() {
    if (this.SculptureTrxId === null || this.isPaymentHandled) {
      return;
    }
    try {
      await this.dataService.updatePaymentByTrxId(this.SculptureTrxId, { is_canceled: true }).toPromise();
    }
    catch (err) {
      console.log('cancel payment failed (ignored)', err);
    }
  }

  private async leaveToPaymentOption() {
    this.stopPaymentPolling();
    this.unsubscribeRealtime();
    await this.cancelPayment();
    this.router.navigate([AppRoute.PAYMENT_OPTION],
      {
        queryParams: {
          promo_type: this.promoType,
          total_price: this.totalPrice,
          vat_price: this.vatPrice,
          discount_price: this.discountPrice,
          deducted_price: this.deductedPrice,
          show_more: true,
          auto_promptpay: false,
        }
      });
  }

  onBack() {
    if (!this.isQrError) {
      this.showBackWarningPopup();
    }
    else {
      this.leaveToPaymentOption();
    }
  }

  showBackWarningPopup() {
    Swal.fire({
      icon: 'warning',
      title: InterfaceText.PAYMENT_SCAN_PAYMENT_CANCEL_TITLE,
      html: InterfaceText.PAYMENT_SCAN_PAYMENT_CANCEL,
      showDenyButton: true,
      confirmButtonText: InterfaceText.PAYMENT_SCAN_PAYMENT_CANCEL_STAY,
      denyButtonText: InterfaceText.PAYMENT_SCAN_PAYMENT_CANCEL_BACK,
    }).then((result) => {
      if (result.isConfirmed) {
        // stay
      } else if (result.isDenied) {
        this.leaveToPaymentOption();
      }
    });
  }

  async showUnknowErrorPopup() {
    const isConfirmed = await this.commonService.showUnknowErrorPopupAndConfirm();
    if (!isConfirmed) {
      this.leaveToPaymentOption();
    }
  }

  onCheck() {
    this.checkPaymentIsPaid();
  }

  checkLabel: string = InterfaceText.PAYMENT_SCAN_CONFIRM_BUTTON;

  /** True while a CONFIRM check is in flight; extra taps are ignored and the button is disabled. */
  isChecking: boolean = false;

  /**
   * CONFIRM: asks the API to check with Omise directly rather than waiting for the webhook to
   * reach the DB. One request per tap, no retries — a failed check says so and the customer
   * taps again; the background poll and realtime subscription keep running meanwhile.
   */
  async checkPaymentIsPaid() {
    if (this.isChecking || this.isPaymentHandled || this.SculptureTrxId === null) {
      return;
    }

    this.isChecking = true;
    this.commonService.isLoading = true;
    this.checkLabel = InterfaceText.PAYMENT_SCAN_CONFIRM_LOADING;
    try {
      const result = await this.dataService.verifyPayment(this.SculptureTrxId).toPromise();
      if (result.status === 'paid') {
        await this.handlePaymentPaid();
      }
      else if (result.status === 'failed') {
        await this.commonService.showPaymentFailedPopup();
        await this.leaveToPaymentOption();
      }
      else {
        this.commonService.showPaymentNotPaidPopup();
      }
    }
    catch (err) {
      console.log('verify payment failed', JSON.stringify(err));
      this.commonService.showPaymentCheckErrorPopup();
    }
    finally {
      this.isChecking = false;
      this.commonService.isLoading = false;
      this.checkLabel = InterfaceText.PAYMENT_SCAN_CONFIRM_BUTTON;
    }
  }

}
